import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export default function Home() {
  // Intro section 'Læs mere' state
  const [introExpanded, setIntroExpanded] = useState(false);

  // Brolægning FAQ accordion state
  const [brolaegningFaqOpen, setBrolaegningFaqOpen] = useState(false);

  // Closing section 'Læs mere / Læs mindre' state
  const [closingExpanded, setClosingExpanded] = useState(false);

  // Contact Form state for intro section
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Honeypot field
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Venligst indtast dit navn';
    if (!formData.phone.trim()) errs.phone = 'Venligst indtast dit telefonnummer';
    if (!formData.email.trim()) {
      errs.email = 'Venligst indtast din e-mailadresse';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errs.email = 'Ugyldig e-mailadresse';
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    setServerError('');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          website_url: formData.website_url // mandatory honeypot
        })
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', phone: '', email: '', message: '', website_url: '' });
      } else {
        const data = await response.json().catch(() => ({}));
        setServerError(data.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen eller ring til os.');
      }
    } catch {
      setServerError('Kunne ikke forbinde til serveren. Prøv venligst igen eller ring på Tlf. 22 23 10 10.');
    } finally {
      setSubmitting(false);
    }
  };

  // 6 specific services for section 4
  const services = [
    {
      title: 'Kloakarbejde',
      slug: '/ydelser/autoriseret-kloakmester',
      image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-24-udgravning-til-faskiner-1.jpeg',
      alt: 'Autoriseret kloakarbejde og udgravning'
    },
    {
      title: 'Belægningsarbejde',
      slug: '/ydelser/belaegningsarbejde',
      image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-26-belaegning-5.jpeg',
      alt: 'Belægningsarbejde og brolægning'
    },
    {
      title: 'Rullegræs',
      slug: '/ydelser/rullegraes',
      image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-27-udgravning-til-baalsted-2_1e2af2c3.jpeg',
      alt: 'Udlægning af rullegræs og haveanlæg'
    },
    {
      title: 'Jordvarme- og luft til vand varmepumper',
      slug: '/ydelser/jordvarme-og-varmepumper',
      image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-24-jordvarme.jpeg',
      alt: 'Jordvarme og luft til vand varmepumper'
    },
    {
      title: 'Gravearbejde og kælderudgravning',
      slug: '/ydelser/gravearbejde',
      image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-26-udgravning-til-indkoersel-1_8b22c2e3.jpeg',
      alt: 'Gravearbejde og kælderudgravning'
    },
    {
      title: 'Fundament og betonarbejde',
      slug: '/ydelser/betonarbejde',
      image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-26-male-worker-screeding-floor-construction-site-651396-2362_54c98602.jpeg',
      alt: 'Fundament og betonarbejde'
    }
  ];

  return (
    <div className="bg-[#dce2d7] text-[#1f241d] overflow-x-hidden">
      <Helmet>
        <title>PR Entreprise & Anlæg ApS | Autoriseret Kloakmester & Entreprenør</title>
        <meta
          name="description"
          content="PR Entreprise & Anlæg ApS udfører autoriseret kloakarbejde, brolægning, betonarbejde og entrepriseprojekter for private og erhverv. Ring Tlf. 22 23 10 10."
        />
        <meta property="og:title" content="PR Entreprise & Anlæg ApS — Autoriseret Kloakmester" />
        <meta property="og:description" content="Kvalitetsbevidst entreprenør og anlægsvirksomhed i Ishøj. Få et godt tilbud." />
      </Helmet>

      {/* =========================================================================
          2. HERO-SEKTION
          - Eksisterende hero-video som baggrund, fylder hele området
          - Mørkt transparent overlay
          - Tekst mod den nederste del
          - Desktop: venstre 70-75%, højre 25-30%
          - Mobil: naturligt stacket
      ========================================================================= */}
      <section className="relative min-h-[82vh] lg:min-h-[88vh] flex flex-col justify-end bg-[#141f14] text-white overflow-hidden border-b border-[#233323]">
        {/* Background Video */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            poster="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/prentreprise-1.jpg"
            className="w-full h-full object-cover"
          >
            <source
              src="https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/pr-entreprise-video.mp4"
              type="video/mp4"
            />
          </video>
          {/* Dark transparent overlay to ensure clear readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/35 to-black/15" />
        </div>

        {/* Content Container (placed towards the bottom) */}
        <div className="relative z-10 max-w-[109rem] mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-12 sm:pb-16 lg:pb-20 w-full">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 lg:gap-12">
            
            {/* Left Area (ca. 70-75%) */}
            <div className="w-full md:w-[68%] lg:w-[72%] space-y-6">
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tight leading-tight text-white drop-shadow-md">
                PR Entreprise & Anlæg
              </h1>

              <p className="text-[#e8eee6] text-base sm:text-lg lg:text-xl leading-relaxed max-w-2xl drop-shadow-sm font-normal">
                PR Entreprise & Anlæg ApS leverer professionelle entreprenør- og kloakløsninger til både private husejere, erhverv og offentlige bygherrer. Vi lægger vægt på stærkt håndværk, god dialog og aftaler der overholdes.
              </p>

              {/* CTAs side by side */}
              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <a
                  href="tel:22231010"
                  className="bg-[#9ecc49] hover:bg-[#8ebb3c] text-[#1f241d] font-bold text-sm px-6 py-3.5 rounded-none transition-colors duration-150 inline-flex items-center justify-center gap-2.5 shadow-md whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone text-[#1f241d]" aria-hidden="true" />
                  <span>Tlf. 22 23 10 10</span>
                </a>

                <Link
                  to="/kontakt"
                  className="bg-[#3d5229] hover:bg-[#2e3f1e] text-white font-bold text-sm px-6 py-3.5 rounded-none transition-colors duration-150 inline-flex items-center justify-center gap-2.5 border border-[#9ecc49]/40 shadow-md whitespace-nowrap"
                >
                  <span>Få et godt tilbud</span>
                  <i className="fa-solid fa-arrow-right text-xs text-[#9ecc49]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Right Area (ca. 25-30%): 4 USPs as a vertical list */}
            <div className="w-full md:w-[32%] lg:w-[28%] border-t md:border-t-0 md:border-l border-white/20 pt-6 md:pt-0 md:pl-6 lg:pl-8">
              <ul className="space-y-4">
                <li className="flex items-center gap-3 text-white text-sm sm:text-base font-semibold">
                  <span className="w-6 h-6 rounded-none bg-[#3d5229] text-[#9ecc49] border border-[#9ecc49]/30 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                  </span>
                  <span>Autoriseret kloakmester</span>
                </li>

                <li className="flex items-center gap-3 text-white text-sm sm:text-base font-semibold">
                  <span className="w-6 h-6 rounded-none bg-[#3d5229] text-[#9ecc49] border border-[#9ecc49]/30 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                  </span>
                  <span>20+ års erfaring</span>
                </li>

                <li className="flex items-center gap-3 text-white text-sm sm:text-base font-semibold">
                  <span className="w-6 h-6 rounded-none bg-[#3d5229] text-[#9ecc49] border border-[#9ecc49]/30 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                  </span>
                  <span>Opstart og levering til tiden</span>
                </li>

                <li className="flex items-center gap-3 text-white text-sm sm:text-base font-semibold">
                  <span className="w-6 h-6 rounded-none bg-[#3d5229] text-[#9ecc49] border border-[#9ecc49]/30 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                  </span>
                  <span>Vi guider dig fra start til slut</span>
                </li>
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================================================
          3. KONTAKT-/INTROSEKTION UNDER HERO
          - Lys sektion med god luft
          - 2 kolonner på tablet og desktop (venstre tekst, højre formular)
      ========================================================================= */}
      <section className="py-16 sm:py-20 lg:py-24 bg-[#f4f7f2] text-[#1f241d] border-b border-[#cbd3c5]">
        <div className="max-w-[109rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-16 items-start">
            
            {/* Left Column: Text & Service Areas */}
            <div className="space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-[#3d5229] bg-[#cbd3c5]/60 px-3 py-1 rounded-none border border-[#3d5229]/20 inline-block">
                BETONARBEJDE & FUNDAMENT
              </span>

              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1f241d] leading-tight">
                Skal du bruge hjælp til betonarbejde og støbning af fundament?
              </h2>

              <p className="text-[#4e564a] text-base leading-relaxed">
                Hos PR Entreprise & Anlæg udfører vi professionelt betonarbejde, opbygning af fundament og støbning af fundament for industrielle og offentlige bygherrer samt private boligejere. Vi sørger altid for et pænt, stabilt og ensartet resultat.
              </p>

              {/* Highlighted Working Areas List */}
              <div className="space-y-3 pt-2">
                <h3 className="font-bold text-sm uppercase tracking-wider text-[#1f241d]">
                  Vi arbejder blandt andet med:
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-sm font-semibold text-[#1f241d]">
                  <div className="flex items-center gap-2.5 bg-white p-3 border border-[#cbd3c5]">
                    <i className="fa-solid fa-circle-check text-[#3d5229]" aria-hidden="true" />
                    <span>Gulvbeton</span>
                  </div>
                  <div className="flex items-center gap-2.5 bg-white p-3 border border-[#cbd3c5]">
                    <i className="fa-solid fa-circle-check text-[#3d5229]" aria-hidden="true" />
                    <span>Udlægning</span>
                  </div>
                  <div className="flex items-center gap-2.5 bg-white p-3 border border-[#cbd3c5]">
                    <i className="fa-solid fa-circle-check text-[#3d5229]" aria-hidden="true" />
                    <span>Udgravning af render</span>
                  </div>
                  <div className="flex items-center gap-2.5 bg-white p-3 border border-[#cbd3c5]">
                    <i className="fa-solid fa-circle-check text-[#3d5229]" aria-hidden="true" />
                    <span>Selvlamineret beton</span>
                  </div>
                  <div className="flex items-center gap-2.5 bg-white p-3 border border-[#cbd3c5]">
                    <i className="fa-solid fa-circle-check text-[#3d5229]" aria-hidden="true" />
                    <span>Støbning af fundament</span>
                  </div>
                  <div className="flex items-center gap-2.5 bg-white p-3 border border-[#cbd3c5]">
                    <i className="fa-solid fa-circle-check text-[#3d5229]" aria-hidden="true" />
                    <span>Opbygning af fundament</span>
                  </div>
                </div>
              </div>

              {/* Fold out text with 'Læs mere' toggle */}
              {introExpanded && (
                <div className="space-y-4 pt-3 text-[#4e564a] text-sm leading-relaxed border-t border-[#cbd3c5] animate-fade-in">
                  <p>
                    Fundamentet danner grundlaget for enhver bygning og skal bære vægten af husets bærende og stabiliserende vægge samt tagkonstruktionen, så huset ikke sætter sig. Samtidig skal fundamentet forhindre, at fugt fra den underliggende jord trænger op i konstruktionen.
                  </p>
                  <p>
                    Vores kompetencer omfatter betonarbejder i alle afskygninger – fra punktfundamenter, sokkelstøbning og terrændæk til støbning af betongulve, trapper og kældre. Med over 20 års erfaring sikrer vi opstart og levering til tiden.
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={() => setIntroExpanded(!introExpanded)}
                className="font-bold text-sm text-[#3d5229] hover:text-[#1f241d] inline-flex items-center gap-1.5 cursor-pointer underline underline-offset-4"
              >
                <span>{introExpanded ? 'Læs mindre' : 'Læs mere'}</span>
                <i className={`fa-solid ${introExpanded ? 'fa-chevron-up' : 'fa-chevron-down'} text-xs`} aria-hidden="true" />
              </button>

              {/* Phone and Email links */}
              <div className="pt-4 border-t border-[#cbd3c5] flex flex-wrap items-center gap-6 text-sm">
                <a
                  href="tel:22231010"
                  className="bg-[#3d5229] hover:bg-[#2e3f1e] text-white font-bold px-5 py-3 rounded-none transition-colors inline-flex items-center gap-2 shadow-sm"
                >
                  <i className="fa-solid fa-phone text-[#9ecc49]" aria-hidden="true" />
                  <span>Tlf. 22 23 10 10</span>
                </a>

                <a
                  href="mailto:info@prentreprise.dk"
                  className="text-[#1f241d] hover:text-[#3d5229] font-semibold inline-flex items-center gap-2 underline underline-offset-4"
                >
                  <i className="fa-regular fa-envelope text-[#3d5229]" aria-hidden="true" />
                  <span>info@prentreprise.dk</span>
                </a>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="bg-white p-6 sm:p-8 lg:p-10 rounded-none border border-[#cbd3c5] shadow-sm">
              <div className="mb-6">
                <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#1f241d]">
                  Indhent et uforpligtende tilbud
                </h3>
                <p className="text-xs sm:text-sm text-[#4e564a] mt-1">
                  Udfyld felterne herunder, så kontakter vi dig hurtigst muligt.
                </p>
              </div>

              {submitted ? (
                <div className="bg-[#f4f7f2] border border-[#3d5229] p-6 text-center space-y-3">
                  <div className="w-12 h-12 bg-[#3d5229] text-[#9ecc49] rounded-full mx-auto flex items-center justify-center text-xl">
                    <i className="fa-solid fa-check" aria-hidden="true" />
                  </div>
                  <h4 className="font-bold text-lg text-[#1f241d]">Tak for din henvendelse!</h4>
                  <p className="text-sm text-[#4e564a]">
                    Vi har modtaget din besked og vender tilbage snarest muligt. Du er også altid velkommen til at ringe på Tlf. 22 23 10 10.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="mt-3 text-xs font-bold text-[#3d5229] hover:underline uppercase tracking-wider"
                  >
                    Send en ny besked
                  </button>
                </div>
              ) : (
                <form onSubmit={handleFormSubmit} noValidate className="space-y-4">
                  {/* Antispam honeypot field */}
                  <input
                    type="text"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleInputChange}
                    className="sr-only absolute"
                    tabIndex={-1}
                    autoComplete="off"
                  />

                  {serverError && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs font-medium">
                      {serverError}
                    </div>
                  )}

                  {/* Navn */}
                  <div>
                    <label htmlFor="form-name" className="block text-xs font-bold uppercase text-[#1f241d] mb-1.5">
                      Navn *
                    </label>
                    <input
                      id="form-name"
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Dit fulde navn"
                      className={`w-full bg-[#fbfcfb] border px-4 py-3 text-sm text-[#1f241d] outline-none transition-colors rounded-none placeholder:text-gray-400 ${
                        formErrors.name ? 'border-red-500' : 'border-[#cbd3c5] focus:border-[#3d5229]'
                      }`}
                    />
                    {formErrors.name && (
                      <p className="text-red-600 text-xs mt-1">{formErrors.name}</p>
                    )}
                  </div>

                  {/* Telefon */}
                  <div>
                    <label htmlFor="form-phone" className="block text-xs font-bold uppercase text-[#1f241d] mb-1.5">
                      Telefon *
                    </label>
                    <input
                      id="form-phone"
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Dit telefonnummer"
                      className={`w-full bg-[#fbfcfb] border px-4 py-3 text-sm text-[#1f241d] outline-none transition-colors rounded-none placeholder:text-gray-400 ${
                        formErrors.phone ? 'border-red-500' : 'border-[#cbd3c5] focus:border-[#3d5229]'
                      }`}
                    />
                    {formErrors.phone && (
                      <p className="text-red-600 text-xs mt-1">{formErrors.phone}</p>
                    )}
                  </div>

                  {/* E-mail */}
                  <div>
                    <label htmlFor="form-email" className="block text-xs font-bold uppercase text-[#1f241d] mb-1.5">
                      E-mail *
                    </label>
                    <input
                      id="form-email"
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Din e-mailadresse"
                      className={`w-full bg-[#fbfcfb] border px-4 py-3 text-sm text-[#1f241d] outline-none transition-colors rounded-none placeholder:text-gray-400 ${
                        formErrors.email ? 'border-red-500' : 'border-[#cbd3c5] focus:border-[#3d5229]'
                      }`}
                    />
                    {formErrors.email && (
                      <p className="text-red-600 text-xs mt-1">{formErrors.email}</p>
                    )}
                  </div>

                  {/* Besked */}
                  <div>
                    <label htmlFor="form-message" className="block text-xs font-bold uppercase text-[#1f241d] mb-1.5">
                      Besked
                    </label>
                    <textarea
                      id="form-message"
                      name="message"
                      rows={5}
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Beskriv kort din opgave, ønsker eller tidsramme..."
                      className="w-full bg-[#fbfcfb] border border-[#cbd3c5] focus:border-[#3d5229] px-4 py-3 text-sm text-[#1f241d] outline-none transition-colors rounded-none placeholder:text-gray-400 resize-none"
                    />
                  </div>

                  {/* Submit button */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#3d5229] hover:bg-[#2e3f1e] text-white font-bold text-sm uppercase tracking-wider py-4 transition-colors rounded-none shadow-sm cursor-pointer disabled:opacity-70"
                  >
                    {submitting ? 'Sender...' : 'Send forespørgsel'}
                  </button>
                </form>
              )}
            </div>

          </div>
        </div>
      </section>

      {/* =========================================================================
          4. SERVICEOVERSIGT
          - Stort visuelt billedgrid
          - Desktop: 3 kolonner, 2 rækker (i alt 6 kort) med meget lille afstand
          - Tablet: 2 kolonner, Mobil: 1 kolonne
          - Høje og billeddominerede kort
          - Mørkt transparent overlay, tekst nederst til venstre
          - Tekst: "VI HJÆLPER MED", stor servicetitel, "Læs mere →"
      ========================================================================= */}
      <section className="py-16 sm:py-20 lg:py-24 bg-[#dce2d7]">
        <div className="max-w-[109rem] mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-12 space-y-2">
            <span className="text-xs font-bold uppercase tracking-widest text-[#3d5229] bg-[#cbd3c5] px-3 py-1 rounded-none border border-[#3d5229]/20 inline-block">
              VORES KOMPETENCER
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1f241d]">
              Vores Primære Serviceområder
            </h2>
            <p className="text-sm text-[#4e564a]">
              Udforsk vores faglige ydelser inden for anlæg, kloak, gravearbejde og energi.
            </p>
          </div>

          {/* 3x2 Grid with small gap */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.slug}
                className="group relative h-80 sm:h-96 lg:h-[420px] overflow-hidden rounded-none border border-[#cbd3c5] bg-gray-900 cursor-pointer block"
              >
                {/* Background Image */}
                <img
                  src={service.image}
                  alt={service.alt}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />

                {/* Dark Transparent Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20 group-hover:bg-[#141f14]/40 transition-colors duration-300 pointer-events-none" />

                {/* Content placed bottom-left */}
                <div className="absolute inset-0 p-6 sm:p-8 flex flex-col justify-end text-white">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-[#9ecc49] mb-1.5 block">
                    VI HJÆLPER MED
                  </span>
                  <h3 className="font-heading text-xl sm:text-2xl font-bold text-white mb-3 group-hover:text-[#9ecc49] transition-colors leading-snug">
                    {service.title}
                  </h3>
                  <div className="inline-flex items-center text-xs font-bold text-white group-hover:text-[#9ecc49] transition-colors">
                    <span>Læs mere</span>
                    <span className="ml-1.5 transition-transform duration-200 group-hover:translate-x-1">→</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

        </div>
      </section>

      {/* =========================================================================
          5. SEKTION OM BROLÆGNING
          - Stort 50/50 split-layout på desktop og tablet
          - Venstre: tekstområde med overskrift "Vi sikrer dig en flot brolægning",
            brødtekst, accordion "Hvad koster brolægning og entreprise?", CTA'er
          - Højre: eksisterende relevant billede fylder hele højden og højre halvdel
      ========================================================================= */}
      <section className="bg-white text-[#1f241d] border-y border-[#cbd3c5] overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2">
          
          {/* Left: Text, Accordion & CTAs */}
          <div className="p-8 sm:p-12 lg:p-16 xl:p-20 flex flex-col justify-center space-y-6">
            <span className="text-xs font-bold uppercase tracking-widest text-[#3d5229] bg-[#f4f7f2] px-3 py-1 rounded-none border border-[#3d5229]/20 self-start">
              BROLÆGNING & BELÆGNING
            </span>

            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1f241d] leading-tight">
              Vi sikrer dig en flot brolægning
            </h2>

            <p className="text-[#4e564a] text-base leading-relaxed">
              Hos PR Entreprise og Anlæg udfører vi alle former for flisearbejde og brolægning hos private og erhverv. Uanset om du skal have lavet en ny indkørsel, en terrasse, en gangsti rundt om huset eller et fortov, står vi altid klar med kompetent rådgivning og solidt håndværk.
            </p>

            <p className="text-[#4e564a] text-base leading-relaxed">
              En professionel brolægger sikrer, at din belægning holder længere. En korrekt bund og præcis hældning minimerer risikoen for, at frost, nedbør og tunge belastninger får fliserne til at knække, blive ustabile eller skæve. Vi sørger for grundig planering, stabiliserende bærelag og flot mønsterlægning.
            </p>

            {/* Price / Enterprise Accordion */}
            <div className="border border-[#cbd3c5] bg-[#fbfcfb] overflow-hidden">
              <button
                type="button"
                onClick={() => setBrolaegningFaqOpen(!brolaegningFaqOpen)}
                className="w-full px-5 py-4 flex justify-between items-center text-left hover:bg-[#f4f7f2] transition-colors cursor-pointer"
                aria-expanded={brolaegningFaqOpen}
              >
                <span className="font-heading font-bold text-sm sm:text-base text-[#1f241d]">
                  Hvad koster brolægning og entreprise?
                </span>
                <span className="text-xl font-bold text-[#3d5229] ml-4 shrink-0 font-mono">
                  {brolaegningFaqOpen ? '−' : '+'}
                </span>
              </button>

              {brolaegningFaqOpen && (
                <div className="px-5 pb-5 pt-1 text-sm text-[#4e564a] leading-relaxed border-t border-[#cbd3c5] bg-white animate-fade-in">
                  Prisen på brolægning og entreprise afhænger af en række faktorer, herunder typen af fliser (f.eks. betonfliser, granit eller natursten), arealets størrelse, tilkørselsforhold samt omfanget af underbundsopbygning og jordarbejde. Vi kommer gerne ud på en uforpligtende besigtigelse og udarbejder et gennemskueligt tilbud med fast pris inden opstart.
                </div>
              )}
            </div>

            {/* CTAs */}
            <div className="pt-2 flex flex-wrap items-center gap-4">
              <a
                href="tel:22231010"
                className="bg-[#3d5229] hover:bg-[#2e3f1e] text-white font-bold text-sm px-6 py-3.5 rounded-none transition-colors inline-flex items-center gap-2 shadow-sm"
              >
                <i className="fa-solid fa-phone text-[#9ecc49]" aria-hidden="true" />
                <span>Tlf. 22 23 10 10</span>
              </a>

              <Link
                to="/kontakt"
                className="bg-[#9ecc49] hover:bg-[#8ebb3c] text-[#1f241d] font-bold text-sm px-6 py-3.5 rounded-none transition-colors inline-flex items-center gap-2 shadow-sm"
              >
                <span>Få et godt tilbud</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>
          </div>

          {/* Right: Full-height image */}
          <div className="relative min-h-[360px] sm:min-h-[440px] lg:min-h-[550px] bg-gray-900">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=80,format=auto/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/belaegning-1.jpg"
              alt="Professionel brolægning og belægningsarbejde"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>

        </div>
      </section>

      {/* =========================================================================
          6. SEKTION "SÅDAN FUNGERER PROCESFORLØBET"
          - Omvendt 50/50 split-layout på desktop og tablet
          - Venstre: stort eksisterende billede, object-fit: cover, fylder hele venstre halvdel
          - Højre: tekstområde med overskrift "Sådan fungerer procesforløbet",
            tre tydelige trin (1. Skriv til os, 2. Vi vender tilbage til dig, 3. Modtagelse af tilbud),
            CTA'er til telefon og kontakt
      ========================================================================= */}
      <section className="bg-[#f4f7f2] text-[#1f241d] border-b border-[#cbd3c5] overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2">
          
          {/* Left: Full-height image */}
          <div className="relative min-h-[360px] sm:min-h-[440px] lg:min-h-[550px] bg-gray-900 order-2 md:order-1">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=80,format=auto/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/prentreprise-1.jpg"
              alt="PR Entreprise & Anlæg proces og maskinpark"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>

          {/* Right: Process Steps & CTAs */}
          <div className="p-8 sm:p-12 lg:p-16 xl:p-20 flex flex-col justify-center space-y-6 order-1 md:order-2">
            <span className="text-xs font-bold uppercase tracking-widest text-[#3d5229] bg-[#cbd3c5]/60 px-3 py-1 rounded-none border border-[#3d5229]/20 self-start">
              TRYGT & GENNEMSKUELIGT
            </span>

            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1f241d] leading-tight">
              Sådan fungerer procesforløbet
            </h2>

            <p className="text-[#4e564a] text-base leading-relaxed">
              Hos PR Entreprise & Anlæg lægger vi vægt på gennemsigtighed, god dialog og aftaler, der holder. Vi gør det let og trygt for dig at komme i mål med dit anlægs-, kloak- eller entrepriseprojekt.
            </p>

            {/* 3 Process Steps */}
            <div className="space-y-4 pt-2">
              {/* Trin 1 */}
              <div className="flex items-start gap-4 p-4 bg-white border border-[#cbd3c5]">
                <div className="w-9 h-9 rounded-none bg-[#3d5229] text-[#9ecc49] font-bold text-base flex items-center justify-center shrink-0">
                  1
                </div>
                <div>
                  <h3 className="font-heading font-bold text-base text-[#1f241d]">
                    Skriv til os
                  </h3>
                  <p className="text-xs sm:text-sm text-[#4e564a] mt-1 leading-relaxed">
                    Tag kontakt via formularen eller ring direkte på Tlf. 22 23 10 10. Fortæl kort om din opgave og dine ønsker.
                  </p>
                </div>
              </div>

              {/* Trin 2 */}
              <div className="flex items-start gap-4 p-4 bg-white border border-[#cbd3c5]">
                <div className="w-9 h-9 rounded-none bg-[#3d5229] text-[#9ecc49] font-bold text-base flex items-center justify-center shrink-0">
                  2
                </div>
                <div>
                  <h3 className="font-heading font-bold text-base text-[#1f241d]">
                    Vi vender tilbage til dig
                  </h3>
                  <p className="text-xs sm:text-sm text-[#4e564a] mt-1 leading-relaxed">
                    Vi gennemgår opgaven og aftaler gerne en besigtigelse på adressen, hvor vi måler op og vurderer forholdene.
                  </p>
                </div>
              </div>

              {/* Trin 3 */}
              <div className="flex items-start gap-4 p-4 bg-white border border-[#cbd3c5]">
                <div className="w-9 h-9 rounded-none bg-[#3d5229] text-[#9ecc49] font-bold text-base flex items-center justify-center shrink-0">
                  3
                </div>
                <div>
                  <h3 className="font-heading font-bold text-base text-[#1f241d]">
                    Modtagelse af tilbud
                  </h3>
                  <p className="text-xs sm:text-sm text-[#4e564a] mt-1 leading-relaxed">
                    Du modtager et gennemskueligt, uforpligtende tilbud med fast pris og tidsplan, så du trygt kan komme i gang.
                  </p>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="pt-2 flex flex-wrap items-center gap-4">
              <a
                href="tel:22231010"
                className="bg-[#3d5229] hover:bg-[#2e3f1e] text-white font-bold text-sm px-6 py-3.5 rounded-none transition-colors inline-flex items-center gap-2 shadow-sm"
              >
                <i className="fa-solid fa-phone text-[#9ecc49]" aria-hidden="true" />
                <span>Tlf. 22 23 10 10</span>
              </a>

              <Link
                to="/kontakt"
                className="bg-[#9ecc49] hover:bg-[#8ebb3c] text-[#1f241d] font-bold text-sm px-6 py-3.5 rounded-none transition-colors inline-flex items-center gap-2 shadow-sm"
              >
                <span>Kontakt os</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* =========================================================================
          7. AFSLUTTENDE TEKSTSEKTION
          - Hvid/lys sektion
          - To kolonner (venstre 35-40%, højre resten)
          - Venstre: overskrift "Planlæg din næste byggeopgave med PR Entreprise"
          - Højre: længere tekst med 'Læs mere / Læs mindre' fold-ud funktion,
            CTA'er til telefon og kontakt
      ========================================================================= */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white text-[#1f241d]">
        <div className="max-w-[109rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-8 lg:gap-16 items-start">
            
            {/* Left Column: ca. 35-40% */}
            <div className="w-full md:w-[38%] space-y-4">
              <span className="text-xs font-bold uppercase tracking-widest text-[#3d5229] bg-[#f4f7f2] px-3 py-1 rounded-none border border-[#3d5229]/20 inline-block">
                ERFAREN ENTREPRENØR & KLOAKMESTER
              </span>

              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1f241d] leading-tight">
                Planlæg din næste byggeopgave med PR Entreprise
              </h2>

              <p className="text-sm text-[#626c5d] leading-relaxed">
                Vi rådgiver og hjælper dig hele vejen igennem projektet – fra de første jordprøver til det færdige resultat.
              </p>
            </div>

            {/* Right Column: ca. 60-65% */}
            <div className="w-full md:w-[62%] space-y-5 text-base text-[#4e564a] leading-relaxed">
              <p>
                PR Entreprise & Anlæg ApS har over 20 års solid erfaring inden for entreprenørbranchen, anlægsgartneri og autoriseret kloakarbejde. Vi har base i Ishøj og servicerer både private grundejere, boligselskaber og erhvervsvirksomheder på hele Sjælland, herunder Storkøbenhavn, Roskilde, Holbæk og Frederikssund.
              </p>

              <p>
                Vi tilbyder totalløsninger såvel som underentrepriser. Vores mangeårige ekspertise gør os i stand til at håndtere hele processen fra jord- og gravearbejde, støbning af fundamenter og kloaktilslutning til afsluttende belægning og haveanlæg. Vi arbejder med moderne maskiner og lægger stor vægt på præcision, ordentlighed og oprydning efter endt arbejde.
              </p>

              {/* Fold out text with 'Læs mere / Læs mindre' */}
              {closingExpanded && (
                <div className="space-y-4 pt-2 border-t border-[#dce2d7] animate-fade-in">
                  <p>
                    Med vores autorisation som kloakmester er du sikret, at alt kloakarbejde udføres strengt efter gældende bygningsreglementer og miljøstandarder. Vi hjælper med alt fra omfangsdræn, faskiner og højvandslukkere til nyanlæg og TV-inspektion. Desuden tilbyder vi installation af luft-til-vand varmepumper og jordvarmeanlæg, der giver langsigtede energibesparelser.
                  </p>
                  <p>
                    Vi tilpasser os gerne din kalender og tilbyder fleksible arbejdstider, herunder arbejde om aftenen og i weekenden ved akutte eller tidskritiske opgaver. Vores mål er altid 100% tilfredshed, god kommunikation undervejs og levering til aftalt tid og pris.
                  </p>
                </div>
              )}

              <div>
                <button
                  type="button"
                  onClick={() => setClosingExpanded(!closingExpanded)}
                  className="font-bold text-sm text-[#3d5229] hover:text-[#1f241d] inline-flex items-center gap-1.5 cursor-pointer underline underline-offset-4"
                >
                  <span>{closingExpanded ? 'Læs mindre' : 'Læs mere'}</span>
                  <i className={`fa-solid ${closingExpanded ? 'fa-chevron-up' : 'fa-chevron-down'} text-xs`} aria-hidden="true" />
                </button>
              </div>

              {/* CTAs */}
              <div className="pt-4 border-t border-[#dce2d7] flex flex-wrap items-center gap-4">
                <a
                  href="tel:22231010"
                  className="bg-[#3d5229] hover:bg-[#2e3f1e] text-white font-bold text-sm px-6 py-3.5 rounded-none transition-colors inline-flex items-center gap-2 shadow-sm"
                >
                  <i className="fa-solid fa-phone text-[#9ecc49]" aria-hidden="true" />
                  <span>Tlf. 22 23 10 10</span>
                </a>

                <Link
                  to="/kontakt"
                  className="bg-[#9ecc49] hover:bg-[#8ebb3c] text-[#1f241d] font-bold text-sm px-6 py-3.5 rounded-none transition-colors inline-flex items-center gap-2 shadow-sm"
                >
                  <span>Kontakt os</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
